import React from 'react'

interface TemperatureData {
  time: number
  value: number
}

interface TemperatureChartProps {
  data: TemperatureData[]
  className?: string
}

export const TemperatureChart: React.FC<TemperatureChartProps> = ({
  data,
  className = ''
}) => {
  const width = 400
  const height = 200
  const padding = { top: 20, right: 20, bottom: 40, left: 50 }
  const chartWidth = width - padding.left - padding.right
  const chartHeight = height - padding.top - padding.bottom

  const normalRange = { min: 36.5, max: 37.5 }
  const minTemp = 30
  const maxTemp = 40
  const timeWindow = 60000 // 60 seconds

  const getXPosition = (timestamp: number) => {
    const now = Date.now()
    const elapsed = now - timestamp
    return chartWidth - (elapsed / timeWindow) * chartWidth
  }

  const getYPosition = (value: number) => {
    return chartHeight - ((value - minTemp) / (maxTemp - minTemp)) * chartHeight
  }

  const getNormalRangeY = (value: number) => {
    return chartHeight - ((value - minTemp) / (maxTemp - minTemp)) * chartHeight
  }

  const normalMinY = getNormalRangeY(normalRange.min)
  const normalMaxY = getNormalRangeY(normalRange.max)

  // Filter data to current time window
  const filteredData = data.filter(d => Date.now() - d.time < timeWindow)

  // Create path for line chart
  const createPath = () => {
    if (filteredData.length < 2) return ''

    const points = filteredData.map(
      d => `${getXPosition(d.time)},${getYPosition(d.value)}`
    )

    return `M${points.join(' L')}`
  }

  const pathData = createPath()

  return (
    <div className={`bg-white rounded-lg ${className}`}>
      <div className="text-base font-semibold text-gray-700 mb-4">
        Temperature (°C)
      </div>

      <svg width={width} height={height} className="overflow-visible">
        {/* Background regions */}
        <rect
          x={padding.left}
          y={padding.top}
          width={chartWidth}
          height={normalMaxY}
          style={{ fill: 'var(--color-chart-acidic-bg)' }}
          opacity={0.5}
        />
        <rect
          x={padding.left}
          y={padding.top + normalMaxY}
          width={chartWidth}
          height={normalMinY - normalMaxY}
          style={{ fill: 'var(--color-chart-normal-temp-bg)' }}
          opacity={0.5}
        />
        <rect
          x={padding.left}
          y={padding.top + normalMinY}
          width={chartWidth}
          height={chartHeight - normalMinY}
          style={{ fill: 'var(--color-chart-low-temp-bg)' }}
          opacity={0.5}
        />

        {/* Grid lines */}
        {[30, 32, 34, 36, 38, 40].map(value => {
          const y = padding.top + getYPosition(value)
          return (
            <line
              key={value}
              x1={padding.left}
              y1={y}
              x2={padding.left + chartWidth}
              y2={y}
              style={{ stroke: 'var(--color-very-light-gray)' }}
              strokeWidth={1}
              opacity={0.5}
            />
          )
        })}

        {/* Normal range lines */}
        <line
          x1={padding.left}
          y1={padding.top + normalMaxY}
          x2={padding.left + chartWidth}
          y2={padding.top + normalMaxY}
          style={{ stroke: 'var(--color-success)' }}
          strokeWidth={2}
        />
        <line
          x1={padding.left}
          y1={padding.top + normalMinY}
          x2={padding.left + chartWidth}
          y2={padding.top + normalMinY}
          style={{ stroke: 'var(--color-success)' }}
          strokeWidth={2}
        />

        {/* Data line */}
        {pathData && (
          <path
            d={pathData}
            fill="none"
            style={{ stroke: 'var(--color-success)' }}
            strokeWidth={2}
            transform={`translate(${padding.left}, ${padding.top})`}
          />
        )}

        {/* Data points */}
        {filteredData.map((point, index) => {
          const x = padding.left + getXPosition(point.time)
          const y = padding.top + getYPosition(point.value)
          const isNormal =
            point.value >= normalRange.min && point.value <= normalRange.max
          const isHigh = point.value > normalRange.max

          let pointColor = 'var(--color-success)' // Normal (green)
          if (isHigh)
            pointColor = 'var(--color-error)' // High (red)
          else if (!isNormal) pointColor = 'var(--color-buffer-blue)' // Low (blue)

          return <circle key={index} cx={x} cy={y} r={3} style={{ fill: pointColor }} />
        })}

        {/* Y-axis labels */}
        {[30, 32, 34, 36, 36.5, 37, 37.5, 38, 40].map(value => {
          const y = padding.top + getYPosition(value)
          return (
            <text
              key={value}
              x={padding.left - 10}
              y={y + 4}
              textAnchor="end"
              className="text-xs fill-gray-500"
            >
              {value}
            </text>
          )
        })}

        {/* X-axis labels */}
        <text
          x={padding.left}
          y={height - 10}
          className="text-xs fill-gray-500"
        >
          0
        </text>
        <text
          x={padding.left + chartWidth * 0.33}
          y={height - 10}
          className="text-xs fill-gray-500"
        >
          25
        </text>
        <text
          x={padding.left + chartWidth * 0.66}
          y={height - 10}
          className="text-xs fill-gray-500"
        >
          50
        </text>
        <text
          x={padding.left + chartWidth}
          y={height - 10}
          className="text-xs fill-gray-700 font-medium"
          textAnchor="end"
        >
          Seconds
        </text>
      </svg>
    </div>
  )
}

export default TemperatureChart
